import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Container,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { ApiError, useAuthControllerLogin, getAuthControllerMeQueryKey } from '@werkbank/shared';
import emblemLogo from '../assets/emblem.png';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const router = useRouter();
  const queryClient = useQueryClient();
  // A2-6: return target set by the 401 redirect. Only accept a same-origin
  // absolute path (no protocol-relative "//host") to avoid open redirects.
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };

  const login = useAuthControllerLogin<ApiError>({
    mutation: {
      onSuccess: (user) => {
        queryClient.setQueryData(getAuthControllerMeQueryKey(), user);
        const target =
          typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')
            ? redirect
            : '/';
        router.history.push(target);
      },
      onError: (error) => {
        if (error.message.includes('TOTP-Code erforderlich')) {
          setNeedsTotp(true);
        }
      },
    },
  });

  const errorMessage =
    login.error && !(needsTotp && !totp && login.error.message.includes('TOTP-Code erforderlich'))
      ? login.error.message
      : null;

  return (
    <div className="wb-login-canvas">
      {/* w="100%" fixiert die Formularbreite auf die 420er-Maxbreite: ohne
          explizite Breite schrumpft der Container im Flex-Canvas auf seine
          Inhaltsbreite und würde erst durch den (längeren) TOTP-Hinweis breiter.
          So bleibt die Breite konstant; TOTP verlängert das Formular nur vertikal. */}
      <Container size={420} w="100%" py={80}>
        {/* Markenmoment (Brief v2.0 §10: Verlauf nur im Login-Hintergrund) */}
        <Stack align="center" gap="xs" mb="sm">
          <img
            src={emblemLogo}
            alt="Holzwerkstätte Schroedter"
            className="wb-logo-emblem"
            style={{ width: 56, height: 56 }}
          />
          <Title order={2} ta="center">
            Werkbank
          </Title>
        </Stack>
        <Text c="dimmed" size="sm" ta="center" mb="lg">
          Holzwerkstätte Schroedter, Anmeldung
        </Text>
        <Paper withBorder p="lg">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              login.mutate({ data: { email, password, totp: totp || undefined } });
            }}
          >
            <Stack gap="md">
              <TextInput
                label="E-Mail-Adresse"
                placeholder="name@firma.de"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                required
                autoFocus
                data-testid="login-email"
              />
              <PasswordInput
                label="Passwort"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                data-testid="login-password"
              />
              {/* D-W1 Welle 3 (LO-2): der Zustandswechsel wird erklärt, statt
                nur wortlos ein neues Feld einzublenden. Die Karte fasst nur den
                sichtbaren Inhalt (E-Mail + Passwort) und wächst erst, wenn der
                zweite Faktor tatsächlich verlangt wird — kein reservierter
                Leerraum. */}
              {needsTotp && (
                <>
                  <Alert variant="light" data-testid="login-totp-hint">
                    Zweiter Faktor erforderlich — bitte zusätzlich den Code aus der
                    Authenticator-App eingeben.
                  </Alert>
                  <TextInput
                    label="TOTP-Code"
                    description="6-stelliger Code aus Ihrer Authenticator-App"
                    value={totp}
                    onChange={(e) => setTotp(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric"
                    required
                    data-testid="login-totp"
                  />
                </>
              )}
              {errorMessage && (
                <Alert color="red" variant="light" data-testid="login-error">
                  {errorMessage}
                </Alert>
              )}
              <Button type="submit" loading={login.isPending} data-testid="login-submit">
                Anmelden
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </div>
  );
}
